import { Fragment, useMemo, useState, type FormEvent } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { canManageLabels, isLabelColorToken, isLabelHexColor, normalizeLabelColor } from '@infras/shared-types';
import { AdminGate } from '../components/AdminGate';
import { Icon } from '../components/Icon';
import { LABEL_COLORS, LabelChip, labelColorOf, labelColorProps, splitLabel } from '../components/labels/LabelChip';
import { labelMatches, toLabelLite } from '../components/labels/LabelPicker';
import { mutationErrorMessage } from '../lib/gqlError';
import { useMe } from '../lib/useMe';
import '../styles/labels.css';

const LABEL_FIELDS = `
  id
  name
  scope
  color
  description
  system
  archivedAt
  createdBy
  createdAt
  usageCount
`;

export const ADMIN_LABELS = gql`
  query AdminLabels {
    labels(includeArchived: true) { ${LABEL_FIELDS} }
  }
`;
const CREATE_LABEL = gql`
  mutation CreateLabel($input: LabelInput!) {
    createLabel(input: $input) { ${LABEL_FIELDS} }
  }
`;
const UPDATE_LABEL = gql`
  mutation UpdateLabel($id: Int!, $input: UpdateLabelInput!) {
    updateLabel(id: $id, input: $input) { ${LABEL_FIELDS} }
  }
`;
const ARCHIVE_LABEL = gql`
  mutation ArchiveLabel($id: Int!, $archived: Boolean) {
    archiveLabel(id: $id, archived: $archived) { ${LABEL_FIELDS} }
  }
`;
const DELETE_LABEL = gql`
  mutation DeleteLabel($id: Int!) {
    deleteLabel(id: $id) { id }
  }
`;

type AdminLabel = {
  id: number;
  name: string;
  scope: string | null;
  color: string;
  description: string | null;
  system: boolean;
  archivedAt: string | null;
  createdBy: string;
  createdAt: string;
  usageCount: number;
};

/** Mirrors core LABEL_NAME_MAX + cleanName + the reserved workflow:: scope. */
const LABEL_NAME_MAX = 64;
const WORKFLOW_SCOPE = 'workflow';

function nameProblem(raw: string): string | null {
  const s = raw.trim();
  if (!s) return 'Name is required.';
  if (s.length > LABEL_NAME_MAX) return `At most ${LABEL_NAME_MAX} characters.`;
  if (s.startsWith('::') || s.endsWith('::')) return 'A name may not start or end with ::.';
  if (splitLabel(s).scope?.toLowerCase() === WORKFLOW_SCOPE) return 'The workflow:: scope is reserved for the board.';
  return null;
}

/** `color` = a token or a custom lowercase `#rrggbb`. */
type FormValues = { name: string; color: string; description: string };

/** Where the Custom swatch starts when the label has no custom colour yet. */
const DEFAULT_CUSTOM = '#5b8def';

/** A hex the user typed → lowercase `#rrggbb`, or null (tokens typed here are not hex). */
const hexOf = (v: string): string | null => {
  const n = normalizeLabelColor(v.trim());
  return n && n.startsWith('#') ? n : null;
};

function LabelForm(props: {
  initial?: AdminLabel;
  submitLabel: string;
  busy: boolean;
  error: string | null;
  onSubmit: (v: FormValues) => void;
  onCancel?: () => void;
}) {
  const { initial, submitLabel, busy, error, onSubmit, onCancel } = props;
  const [name, setName] = useState(initial?.name ?? '');
  const initColor = initial?.color ?? 'gray';
  const [color, setColor] = useState<string>(isLabelHexColor(initColor) ? initColor : labelColorOf(initColor));
  const [customHex, setCustomHex] = useState(isLabelHexColor(initColor) ? initColor : DEFAULT_CUSTOM);
  const [hexText, setHexText] = useState(customHex);
  const isCustom = !isLabelColorToken(color);
  const hexProblem = isCustom && !hexOf(hexText) ? 'Enter a hex colour such as #3b82f6 or #38f.' : null;
  const pickHex = (hex: string) => {
    setCustomHex(hex);
    setColor(hex);
  };
  const [description, setDescription] = useState(initial?.description ?? '');
  const [touched, setTouched] = useState(false);
  const problem = nameProblem(name);
  const preview = splitLabel(name.trim());
  const scopeChange =
    !!initial && initial.usageCount > 0 && (preview.scope ?? null)?.toLowerCase() !== (initial.scope ?? null)?.toLowerCase();
  const fid = initial ? `label-${initial.id}` : 'label-new';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (problem || hexProblem) return;
    onSubmit({ name: name.trim(), color, description: description.trim() });
  };

  return (
    <form className="label-form" onSubmit={submit} noValidate>
      <div className="label-form__grid">
        <div className="access-field">
          <label htmlFor={`${fid}-name`}>Name</label>
          <input
            id={`${fid}-name`}
            value={name}
            maxLength={LABEL_NAME_MAX + 10}
            placeholder="blocked or team::db"
            aria-invalid={(touched && !!problem) || undefined}
            aria-describedby={`${fid}-name-hint`}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
          />
          <div id={`${fid}-name-hint`} className={`label-form__hint${(touched && problem) || scopeChange ? ' label-form__hint--warn' : ''}`}>
            {touched && problem
              ? problem
              : scopeChange
                ? `In use on ${initial!.usageCount} task(s) — a rename may not change its scope (${initial!.scope ?? 'none'}).`
                : 'Use scope::value for a scoped label — a task keeps one label per scope.'}
          </div>
        </div>
        <div className="access-field">
          <label htmlFor={`${fid}-desc`}>Description</label>
          <input id={`${fid}-desc`} value={description} placeholder="Optional" onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>
      <fieldset className="label-swatches">
        <legend>Colour</legend>
        {LABEL_COLORS.map((c) => (
          <label key={c} className={`label-swatch label-chip--${c}`}>
            <input type="radio" name={`${fid}-color`} value={c} checked={color === c} onChange={() => setColor(c)} />
            {c}
          </label>
        ))}
        <label className={`label-swatch ${labelColorProps(customHex).className}`} style={labelColorProps(customHex).style}>
          <input type="radio" name={`${fid}-color`} value="custom" checked={isCustom} onChange={() => setColor(customHex)} />
          Custom
        </label>
        {isCustom ? (
          <span className="label-custom">
            <input
              type="color"
              id={`${fid}-picker`}
              aria-label="Custom colour"
              value={customHex}
              onChange={(e) => {
                pickHex(e.target.value.toLowerCase());
                setHexText(e.target.value.toLowerCase());
              }}
            />
            <input
              type="text"
              id={`${fid}-hex`}
              aria-label="Hex colour"
              value={hexText}
              maxLength={7}
              spellCheck={false}
              autoComplete="off"
              placeholder="#3b82f6"
              aria-invalid={!!hexProblem || undefined}
              aria-describedby={hexProblem ? `${fid}-hex-hint` : undefined}
              onChange={(e) => {
                setHexText(e.target.value);
                const h = hexOf(e.target.value);
                if (h) pickHex(h);
              }}
              onBlur={() => {
                const h = hexOf(hexText);
                if (h) setHexText(h);
              }}
            />
            {hexProblem ? (
              <span id={`${fid}-hex-hint`} className="label-form__hint label-form__hint--warn">
                {hexProblem}
              </span>
            ) : null}
          </span>
        ) : null}
      </fieldset>
      <div className="label-form__preview" aria-live="polite">
        Preview
        {name.trim() ? (
          <LabelChip label={{ id: 'preview', name: name.trim(), scope: preview.scope, color }} />
        ) : (
          <span>—</span>
        )}
        {preview.scope ? <span>scope “{preview.scope}”, value “{preview.value}”</span> : null}
      </div>
      {error ? (
        <div className="label-form__error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="label-form__actions">
        {/* An invalid custom hex blocks the submit outright — the hint above says why. */}
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !!hexProblem}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        {onCancel ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}

export function LabelsPage() {
  const { me } = useMe();
  const allowed = !!me && canManageLabels(me.role);
  const { data, loading, error, refetch } = useQuery<{ labels: AdminLabel[] }>(ADMIN_LABELS, {
    skip: !allowed,
    fetchPolicy: 'cache-and-network',
  });
  const [createLabel, createState] = useMutation(CREATE_LABEL);
  const [updateLabel, updateState] = useMutation(UPDATE_LABEL);
  const [archiveLabel] = useMutation(ARCHIVE_LABEL);
  const [deleteLabel] = useMutation(DELETE_LABEL);

  const [q, setQ] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [editErr, setEditErr] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<{ id: number; msg: string } | null>(null);
  const [rowBusy, setRowBusy] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const rows = useMemo(() => {
    const all = data?.labels ?? [];
    return all
      .filter((l) => showArchived || !l.archivedAt)
      .filter((l) => !q.trim() || labelMatches(l.name, q) || (l.description ?? '').toLowerCase().includes(q.trim().toLowerCase()))
      .sort((a, b) => Number(b.system) - Number(a.system) || a.name.localeCompare(b.name));
  }, [data, q, showArchived]);
  const archivedCount = (data?.labels ?? []).filter((l) => l.archivedAt).length;

  const errOf = (e: unknown) => mutationErrorMessage(e as Parameters<typeof mutationErrorMessage>[0]) ?? String(e);

  const doCreate = async (v: FormValues) => {
    setCreateErr(null);
    try {
      await createLabel({ variables: { input: { name: v.name, color: v.color, description: v.description || null } } });
      setCreating(false);
      setNotice(`Created ${v.name}.`);
      await refetch();
    } catch (e) {
      setCreateErr(errOf(e));
    }
  };

  const doUpdate = async (l: AdminLabel, v: FormValues) => {
    setEditErr(null);
    const input: Record<string, string | null> = {};
    if (v.name !== l.name) input.name = v.name;
    if (v.color !== l.color) input.color = v.color;
    if ((v.description || null) !== (l.description || null)) input.description = v.description || null;
    if (!Object.keys(input).length) {
      setEditing(null);
      return;
    }
    try {
      await updateLabel({ variables: { id: l.id, input } });
      setEditing(null);
      setNotice(`Saved ${v.name}.`);
    } catch (e) {
      setEditErr(errOf(e));
    }
  };

  const rowAction = async (l: AdminLabel, fn: () => Promise<unknown>, done: string, refresh = false) => {
    setRowErr(null);
    setRowBusy(l.id);
    try {
      await fn();
      setNotice(done);
      if (refresh) await refetch();
    } catch (e) {
      setRowErr({ id: l.id, msg: errOf(e) });
    } finally {
      setRowBusy(null);
    }
  };

  return (
    <AdminGate allowed={allowed} need="root users and maintainers">
      <div className="page page--wide stack labels-page">
        <div className="detail-head labels-head">
          <h2>Labels</h2>
          <div className="spacer" />
          {!creating ? (
            <button type="button" className="btn btn-primary" onClick={() => (setCreating(true), setCreateErr(null))}>
              + New label
            </button>
          ) : null}
        </div>
        <p className="detail-desc labels-intro">
          Labels are free text. A name with <code>::</code> is scoped: the part before the last <code>::</code> is its scope,
          and a task carries one label per scope. System labels drive the board and cannot be changed. A label in use
          can be archived (it stays on its tasks); a label that is not on any task can be deleted, and the timeline
          keeps its name on past entries.
        </p>
        {creating ? (
          <LabelForm submitLabel="Create label" busy={createState.loading} error={createErr} onSubmit={doCreate} onCancel={() => setCreating(false)} />
        ) : null}
        <div className="toolbar labels-toolbar">
          <input type="search" aria-label="Filter labels" placeholder="Filter labels" value={q} onChange={(e) => setQ(e.target.value)} />
          <label>
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Show archived ({archivedCount})
          </label>
        </div>
        {notice ? (
          <div className="muted text-xs" role="status">
            {notice}
          </div>
        ) : null}
        {error ? <div className="error">Could not load labels: {error.message}</div> : null}
        {loading && !data ? <div className="muted">Loading labels…</div> : null}
        {data ? (
          rows.length ? (
            <div className="labels-table-wrap">
              <table className="data-list--primary labels-table" aria-label="Labels">
                <thead>
                  <tr>
                    <th scope="col">Label</th>
                    <th scope="col">Description</th>
                    <th scope="col" className="labels-table__num">Used on</th>
                    <th scope="col" className="labels-table__actions">
                      <span className="labels-sr">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((l) => {
                    const busy = rowBusy === l.id;
                    return (
                      <Fragment key={l.id}>
                        <tr className={`labels-row${l.archivedAt ? ' labels-row--archived' : ''}`} data-testid={`label-row-${l.id}`}>
                          <td className="labels-row__name">
                            <span className="labels-row__chips">
                              <LabelChip label={toLabelLite(l)} />
                              {l.system ? <span className="labels-meta">system</span> : null}
                              {l.archivedAt ? <span className="labels-meta">archived</span> : null}
                            </span>
                          </td>
                          <td className={`labels-row__desc${l.description ? '' : ' labels-row__desc--empty'}`}>{l.description}</td>
                          <td className="labels-row__usage labels-table__num">
                            {l.usageCount} {l.usageCount === 1 ? 'task' : 'tasks'}
                          </td>
                          <td className="labels-table__actions">
                            <span className="labels-row__actions">
                              {l.system ? (
                                <span className="muted text-xs">Read-only</span>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    aria-label={`Edit ${l.name}`}
                                    disabled={busy}
                                    onClick={() => (setEditing(editing === l.id ? null : l.id), setEditErr(null))}
                                  >
                                    <Icon name="edit" size={14} /> Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    disabled={busy}
                                    onClick={() =>
                                      void rowAction(
                                        l,
                                        () => archiveLabel({ variables: { id: l.id, archived: !l.archivedAt } }),
                                        l.archivedAt ? `Unarchived ${l.name}.` : `Archived ${l.name}.`,
                                      )
                                    }
                                  >
                                    {l.archivedAt ? 'Unarchive' : 'Archive'}
                                  </button>
                                  {/* Same rule as core deleteLabel: not currently applied to any task. */}
                                  {l.usageCount === 0 ? (
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-sm labels-delete"
                                      aria-label={`Delete ${l.name}`}
                                      disabled={busy}
                                      onClick={() => {
                                        if (!window.confirm(`Delete the label "${l.name}"? This cannot be undone; past timeline entries keep its name.`)) return;
                                        void rowAction(l, () => deleteLabel({ variables: { id: l.id } }), `Deleted ${l.name}.`, true);
                                      }}
                                    >
                                      <Icon name="trash" size={14} /> Delete
                                    </button>
                                  ) : null}
                                </>
                              )}
                            </span>
                            {rowErr?.id === l.id ? (
                              <div className="labels-row__error label-form__error" role="alert">
                                {rowErr.msg}
                              </div>
                            ) : null}
                          </td>
                        </tr>
                        {editing === l.id ? (
                          <tr className="labels-edit-row">
                            <td colSpan={4}>
                              <LabelForm
                                initial={l}
                                submitLabel="Save"
                                busy={updateState.loading}
                                error={editErr}
                                onSubmit={(v) => void doUpdate(l, v)}
                                onCancel={() => setEditing(null)}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="muted">{q ? `No label matches “${q}”.` : 'No labels yet.'}</div>
          )
        ) : null}
      </div>
    </AdminGate>
  );
}

export default LabelsPage;
